import { Box, Button, Container, Typography } from "@mui/material";
import Seeds from "../../components/Seeds";

const HowItWorksStep = ({
  number,
  text1,
  text2,
  icon,
}: {
  number: number;
  text1: string;
  text2: string;
  icon: string;
}) => (
  <Box
    sx={(theme) => ({
      bgcolor: "white",
      borderRadius: 3,
      p: 3,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      transition: "all 0.3s ease",
      position: "relative",
      overflow: "hidden",
      boxSizing: "border-box",
      flex: "0 1 calc(33.33% - 1.5rem)",
      [theme.breakpoints.down("md")]: {
        flex: "0 1 calc(50% - 1.5rem)",
      },
      [theme.breakpoints.down("sm")]: {
        flex: "0 1 calc(100% - 1.5rem)",
      },
      "&:hover": {
        transform: "translateY(-8px)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      },
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "4px",
        bgcolor: "primary.main",
      },
    })}
  >
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: "50%",
        bgcolor: "primary.main",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1.75rem",
        fontWeight: "bold",
        mb: 2,
        boxShadow: "0 4px 8px rgba(68, 119, 55, 0.3)",
      }}
    >
      {number}
    </Box>
    <Box
      sx={{
        mb: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: 140,
      }}
    >
      <img
        src={icon}
        alt={text1}
        style={{ maxHeight: "100%", maxWidth: "100%" }}
      />
    </Box>
    <Typography
      variant="body1"
      sx={{
        fontWeight: 600,
        color: "primary.main",
        mb: 1.5,
        lineHeight: 1.4,
        minHeight: "3em",
      }}
    >
      {text1}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: "text.secondary",
        lineHeight: 1.6,
        fontSize: "0.9rem",
      }}
    >
      {text2}
    </Typography>
  </Box>
);

const Home = () => {
  return (
    <Box sx={{ bgcolor: "background.default" }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          color: "white",
          py: { xs: 8, md: 12 },
          textAlign: "center",
          backgroundImage: "url(/home.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(68, 119, 55, 0.25)",
            zIndex: 1,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 3,
              fontSize: { xs: "2rem", md: "3.5rem" },
              textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
            }}
          >
            Conectamos campo e cidade levando as feiras ecológicas até você
          </Typography>
          <Typography
            variant="h5"
            sx={{
              mb: 4,
              fontWeight: 400,
              opacity: 0.95,
              fontSize: { xs: "1.2rem", md: "1.5rem" },
              textShadow: "1px 1px 3px rgba(0,0,0,0.3)",
            }}
          >
            Vem conosco!
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              bgcolor: "white",
              color: "primary.main",
              px: 6,
              py: 2,
              fontSize: "1.25rem",
              fontWeight: 600,
              boxShadow: "0 4px 6px rgba(0,0,0,0.2)",
              "&:hover": {
                bgcolor: "background.default",
                boxShadow: "0 6px 8px rgba(0,0,0,0.3)",
              },
            }}
            onClick={() => {
              document
                .getElementById("feiras")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Escolha sua semente
          </Button>
        </Container>
      </Box>

      {/* Como Funciona Section */}
      <Box sx={{ bgcolor: "background.default", py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              textAlign: "center",
              fontWeight: 700,
              mb: 2,
              color: "primary.main",
            }}
          >
            Como funciona a feira online?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 6,
              color: "text.secondary",
              fontSize: "1.1rem",
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            Do campo até sua casa em 5 passos simples
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 3,
              justifyContent: "center",
            }}
          >
            <HowItWorksStep
              number={1}
              text1="Famílias agricultoras locais cultivam alimentos agroecologicamente."
              text2="Cuidado com a terra, água e vida no planeta. Preservação da nossa biodiversidade."
              icon="como-funciona-1.png"
            />
            <HowItWorksStep
              number={2}
              text1="Você escolhe a semente mais próxima de você e seleciona os alimentos desejados. Todos com certificação orgânica."
              text2="Lista nova toda semana respeitando a sazonalidade."
              icon="como-funciona-2.png"
            />
            <HowItWorksStep
              number={3}
              text1="Enviamos os pedidos para as agricultoras e coletamos os alimentos nos dias das entregas."
              text2="Cadeia mais curta de comercialização."
              icon="como-funciona-3.png"
            />
            <HowItWorksStep
              number={4}
              text1="Você recebe a feira conforme o método de entrega escolhido."
              text2="Mais saúde, qualidade e diversidade no prato. Da agricultura familiar para a sua família."
              icon="como-funciona-4.png"
            />
            <HowItWorksStep
              number={5}
              text1="Você devolve caixa, garrafas, redinhas e demais embalagens."
              text2="Nós devolvemos para as produtoras. O ciclo recomeça."
              icon="como-funciona-5.png"
            />
          </Box>
        </Container>
      </Box>

      {/* Rede de Reciprocidade Section */}
      <Box sx={{ bgcolor: "background.paper", py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              textAlign: "center",
              fontWeight: 700,
              mb: 6,
              color: "primary.main",
            }}
          >
            Ajuda mútua
          </Typography>
          <Box
            sx={(theme) => ({
              display: "flex",
              gap: 4,
              alignItems: "center",
              [theme.breakpoints.down("md")]: {
                flexDirection: "column",
              },
            })}
          >
            <Box
              sx={(theme) => ({
                flex: "1 1 80%",
                [theme.breakpoints.down("md")]: {
                  flex: "1 1 100%",
                  order: 2,
                },
              })}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.8,
                  color: "text.primary",
                }}
              >
                A Multifeiras é uma rede de ajuda mútua alimentar que conecta
                agricultores agroecológicos e parceiras urbanas sem
                atravessadores. Ao organizar pedidos coletivos, garantimos que
                quem planta receba um preço justo e que quem consome tenha
                acesso direto a alimentos saudáveis. Mais do que uma plataforma,
                somos uma comunidade que se apoia para fortalecer a agroecologia
                e a soberania alimentar.
              </Typography>
            </Box>
            <Box
              sx={(theme) => ({
                flex: "0 0 20%",
                [theme.breakpoints.down("md")]: {
                  flex: "1 1 100%",
                  order: 1,
                  maxWidth: "400px",
                },
              })}
            >
              <img
                src="/entrega.png"
                alt="Entrega de alimentos"
                style={{
                  width: "100%",
                  height: "auto",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Quem Faz a Rede Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          sx={{
            textAlign: "center",
            fontWeight: 700,
            mb: 6,
            color: "primary.main",
          }}
        >
          Quem faz a rede?
        </Typography>
        <Box
          sx={(theme) => ({
            display: "flex",
            gap: 4,
            alignItems: "center",
            [theme.breakpoints.down("md")]: {
              flexDirection: "column",
            },
          })}
        >
          <Box
            sx={(theme) => ({
              flex: "0 0 40%",
              [theme.breakpoints.down("md")]: {
                flex: "1 1 100%",
              },
            })}
          >
            <img
              src="/familia-rocha-renz.jpg"
              alt="Família Rocha Renz"
              style={{
                width: "100%",
                height: "auto",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
            />
          </Box>
          <Box
            sx={(theme) => ({
              flex: "1 1 60%",
              [theme.breakpoints.down("md")]: {
                flex: "1 1 100%",
              },
            })}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: "1.1rem",
                lineHeight: 1.8,
                color: "text.primary",
              }}
            >
              Reunimos nessa rede pessoas que estão dispostas a construir uma
              alternativa mais consciente para viver, por isso nos denominamos
              parceiros urbanos e nos conectamos com as famílias rurais que
              cultivam a Agroecologia como filosofia de produção e formalizam a
              certificação orgânica de sua produção.
              <br />
              <br />
              As protagonistas são as agricultoras e agricultores que trabalham
              na terra para garantir alimentos saudáveis e de qualidade para
              todas. E para nós o alimento não é apenas nutricional, mas sim
              relacional, pois nele estão contidas as relações e os afetos que
              fizeram parte de sua produção.
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* Feiras Section */}
      <Box sx={{ bgcolor: "background.paper", py: 8 }} id="feiras">
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              textAlign: "center",
              fontWeight: 700,
              mb: 2,
              color: "primary.main",
            }}
          >
            Sementes
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: "1.1rem",
              lineHeight: 1.8,
              color: "text.primary",
              textAlign: "center",
              maxWidth: "900px",
              mx: "auto",
              mb: 4,
            }}
          >
            <strong style={{ color: "#447737" }}>Sementes</strong> são os pontos
            de distribuição da nossa rede, lugares onde os alimentos chegam
            frescos das famílias agricultoras e de onde partem para sua casa.
            Cada semente é cuidada por uma{" "}
            <strong style={{ color: "#447737" }}>semeadora</strong> – uma pessoa
            da comunidade que organiza os pedidos, cuida da logística e mantém
            viva a conexão entre campo e cidade. Escolha a semente mais próxima
            de você e conheça quem faz essa ponte acontecer! 🌱
          </Typography>
          <Seeds />
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          bgcolor: "primary.main",
          color: "white",
          py: 4,
          textAlign: "center",
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body1">
            © {new Date().getFullYear()} - Multifeiras
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
